/**
 * Vonalkód-formátum — a rendszer legfizikaibb kontraktusa.
 *
 * Ez a formátum rá lesz nyomtatva 4000+ DataMatrix címkére, és be van fordítva
 * az Android appba (BarcodeParser.kt). A számjegy-szélesség megváltoztatása
 * utólag az összes címke újranyomtatását jelenti — ezért van CHECK constraint
 * az adatbázisban is, és ezért ez a fájl az egyetlen igazságforrás.
 *
 * A típus-előtag célja, hogy a kliens SZERVERHÍVÁS NÉLKÜL eldönthesse, mit
 * olvasott be. Ez teszi lehetővé a spec 17.2 §-ának <1 mp visszajelzését és a
 * "rossz típusú vonalkód" azonnali elutasítását.
 *
 * A MAP- számtartomány KÖZÖS a későbbi dokumentum-digitalizálási fázissal
 * (ld. inputs/PERSPED_DIGITALIZALAS.md): egy mappa = egy azonosító, örökre.
 */
export const BarcodeKind = {
    FOLDER: 'FOLDER',
    PALLET: 'PALLET',
    /** Polc — fix szintekkel rendelkező lokáció. */
    SHELF: 'SHELF',
    /**
     * Tároló — szint nélküli lokáció. A G fázisig `LOCATION` volt `LOC-`
     * előtaggal; a névváltás azt tükrözi, hogy már nem „minden egyéb
     * lokáció" gyűjtőtípus, hanem a három egyenrangú típus egyike.
     */
    STORAGE: 'STORAGE',
};
/**
 * Előtag → típus, és a hozzá tartozó számjegy-szélesség.
 *
 * A LOKÁCIÓK 4 SZÁMJEGYRE VÁLTOTTAK (G fázis, 2026-08-14). A 3 számjegy
 * 999 darabnál elfogyott volna, és a tartomány bővítése utólag minden
 * lokációcímke újranyomtatását jelentette volna. 2026-08-14-én egyetlen
 * lokációcímke sem volt fizikailag felragasztva — ez volt az utolsó
 * pillanat, amikor a váltás ingyen van.
 *
 * A `MAP-` VÁLTOZATLANUL 5 számjegy: ez a tartomány közös a későbbi
 * dokumentum-digitalizálási fázissal (inputs/PERSPED_DIGITALIZALAS.md),
 * ahol már megállapodás tárgya.
 */
export const BARCODE_FORMATS = {
    FOLDER: { prefix: 'MAP-', digits: 5, pattern: /^MAP-\d{5}$/ },
    PALLET: { prefix: 'RKL-', digits: 4, pattern: /^RKL-\d{4}$/ },
    SHELF: { prefix: 'POL-', digits: 4, pattern: /^POL-\d{4}$/ },
    STORAGE: { prefix: 'TAR-', digits: 4, pattern: /^TAR-\d{4}$/ },
};
/** Bármely érvényes vonalkód. Az adatbázis CHECK constraintjei ezt tükrözik. */
export const ANY_BARCODE_PATTERN = /^(MAP-\d{5}|RKL-\d{4}|POL-\d{4}|TAR-\d{4})$/;
/**
 * Beolvasott nyers string értelmezése.
 *
 * Normalizál, mert a szkenner és a kézi bevitel is szennyezhet: a DataWedge
 * időnként sorvéget fűz a végére, a kézi bevitel pedig kisbetűs lehet.
 * A normalizálás SZÁNDÉKOSAN nem megengedő: hiányzó vezető nullát
 * (pl. "MAP-423") nem javítunk ki, mert az elfedne egy valódi címkehibát.
 */
export function parseBarcode(raw) {
    const normalized = raw.trim().toUpperCase();
    if (normalized.length === 0) {
        return { ok: false, raw, reason: 'EMPTY' };
    }
    for (const kind of Object.keys(BARCODE_FORMATS)) {
        const format = BARCODE_FORMATS[kind];
        if (format.pattern.test(normalized)) {
            return {
                ok: true,
                kind,
                barcode: normalized,
                sequence: Number.parseInt(normalized.slice(format.prefix.length), 10),
            };
        }
    }
    return { ok: false, raw, reason: 'UNKNOWN_FORMAT' };
}
/** Igaz, ha a vonalkód érvényes ÉS a megadott típusú. */
export function isBarcodeOfKind(raw, kind) {
    const parsed = parseBarcode(raw);
    return parsed.ok && parsed.kind === kind;
}
/**
 * Sorszámból vonalkód. A címkenyomtatás és a seed adatok használják.
 * @throws ha a sorszám nem fér el a típushoz tartozó számjegy-szélességben.
 */
export function formatBarcode(kind, sequence) {
    const { prefix, digits } = BARCODE_FORMATS[kind];
    if (!Number.isInteger(sequence) || sequence < 0) {
        throw new Error(`A sorszám nem lehet negatív vagy tört: ${sequence}`);
    }
    const max = 10 ** digits - 1;
    if (sequence > max) {
        throw new Error(`A(z) ${sequence} sorszám nem fér el ${digits} számjegyen (max ${max}, típus: ${kind})`);
    }
    return prefix + String(sequence).padStart(digits, '0');
}
//# sourceMappingURL=barcode.js.map